import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShieldAlert, ArrowRight, Lock, PlayCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";
  const [email, setEmail] = useState("operator@sentinel.io");
  const [password, setPassword] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    login(email);
    navigate(from, { replace: true });
  };

  const handleDemo = () => {
    login("demo@sentinel.io");
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-sentinel-bg lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden overflow-hidden border-r border-sentinel-border lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div className="absolute inset-0 grid-texture opacity-40" />
        <Link to="/" className="relative flex items-center gap-2.5" data-testid="login-brand-home-link">
          <span className="flex h-8 w-8 items-center justify-center rounded border border-sentinel-border bg-sentinel-accent/10">
            <ShieldAlert className="h-4 w-4 text-sentinel-accent" strokeWidth={2.2} />
          </span>
          <span className="font-heading text-base font-bold tracking-[0.22em] text-slate-100">
            SENTINEL
          </span>
        </Link>

        <div className="relative max-w-md">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sentinel-accent">
            Operational Access
          </p>
          <h1 className="mt-4 font-heading text-3xl font-semibold leading-tight tracking-tight text-slate-100">
            Context-aware safety intelligence, always on watch.
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            Sign in to review your safety state, incident history and emergency
            configuration. Sentinel continuously evaluates sensor context to
            surface abnormal situations before they escalate.
          </p>
        </div>

        <div className="relative flex items-center gap-6 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
          <span className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Systems nominal
          </span>
          <span>Demo Environment</span>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <Link to="/" className="flex items-center gap-2.5" data-testid="login-brand-home-link-mobile">
              <span className="flex h-8 w-8 items-center justify-center rounded border border-sentinel-border bg-sentinel-accent/10">
                <ShieldAlert className="h-4 w-4 text-sentinel-accent" strokeWidth={2.2} />
              </span>
              <span className="font-heading text-base font-bold tracking-[0.22em] text-slate-100">
                SENTINEL
              </span>
            </Link>
          </div>

          <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-100">
            Sign in
          </h2>
          <p className="mt-1.5 text-sm text-slate-400">
            Access your Sentinel operational dashboard.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="h-11 border-sentinel-border bg-sentinel-deep text-slate-100 focus-visible:ring-sentinel-accent"
                placeholder="you@organisation.io"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="h-11 border-sentinel-border bg-sentinel-deep text-slate-100 focus-visible:ring-sentinel-accent"
                placeholder="••••••••••"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="group h-11 w-full bg-sentinel-accent font-medium text-white hover:bg-sentinel-accenthover"
            >
              Sign in
              <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>

            <p className="flex items-center justify-center gap-1.5 font-mono text-[11px] text-slate-600">
              <Lock className="h-3 w-3" />
              Production sign-in — any credentials proceed
            </p>
          </form>

          {/* Demo bypass — separate from production sign-in */}
          <div className="mt-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-sentinel-border" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
              or
            </span>
            <span className="h-px flex-1 bg-sentinel-border" />
          </div>

          <Button
            type="button"
            onClick={handleDemo}
            variant="outline"
            data-testid="demo-dashboard-button"
            className="group mt-6 h-11 w-full border-sentinel-border bg-transparent font-medium text-slate-200 hover:border-sentinel-borderhover hover:bg-[#1C2030] hover:text-white"
          >
            <PlayCircle className="mr-2 h-4 w-4 text-sentinel-accent" />
            Demo / Preview Dashboard
            <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
          <p className="mt-2 text-center font-mono text-[11px] text-slate-600">
            Opens the dashboard with sample data — no account needed
          </p>

          <p className="mt-8 text-center text-sm text-slate-500">
            <Link to="/" className="text-slate-400 underline-offset-4 hover:text-slate-200 hover:underline" data-testid="login-back-link">
              Return to homepage
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
